import { dynamic, enter, memory, spatial } from '@dce-front/one-navigation';
import type { BinderMiddlewareOptions } from '../hooks/useBinderMiddleware';
import {
  centerBinder,
  pageScrollTop,
  scroll,
  scrollFocusedIntoHorizontalList,
  scrollFocusedIntoVerticalScrollContainer,
  scrollFocusedIntoVerticalViewport,
  scrollModalTop,
  scrollTop,
  scrollTopOrCenter,
  scrollToTopOnFirstLineOfContentGrid,
} from './scroll';

const keepFocusedItemOnTheLeft = (
  featNewTVNavigation: boolean,
  options?: BinderMiddlewareOptions,
) =>
  featNewTVNavigation
    ? [memory(), enter({ forceFocusIndex: options?.itemOffset ?? 0 })]
    : [];

export const MIDDLEWARE_HEADER = [scroll([pageScrollTop]), memory(), spatial()];

export const MIDDLEWARE_CENTER = [scroll([centerBinder]), spatial()];

// forceFocus on the "a" letter
export const MIDDLEWARE_KEYBOARD = [
  enter({ forceFocusIndex: 1 }),
  scroll([pageScrollTop]),
  memory(),
  spatial(),
];

export const MIDDLEWARE_STRATE = [
  scroll([
    scrollFocusedIntoVerticalViewport(),
    scrollFocusedIntoHorizontalList(),
  ]),
  spatial(),
];

export const getMiddlewareStrate = (
  featNewTVNavigation: boolean,
  options?: BinderMiddlewareOptions,
) => [
  scroll([
    scrollFocusedIntoVerticalViewport({
      featNewTVNavigation,
    }),
    scrollFocusedIntoHorizontalList(featNewTVNavigation),
  ]),
  ...keepFocusedItemOnTheLeft(featNewTVNavigation, options),
  spatial(),
];

export const getMiddlewareContentGridTop = (featNewTVNavigation: boolean) => [
  scroll([
    scrollFocusedIntoVerticalViewport({
      featNewTVNavigation,
    }),
    scrollFocusedIntoHorizontalList(featNewTVNavigation),
    scrollToTopOnFirstLineOfContentGrid(),
  ]),
  ...keepFocusedItemOnTheLeft(featNewTVNavigation),
  spatial(),
];

export const MIDDLEWARE_STRATE_TOP = [
  scroll([pageScrollTop, scrollFocusedIntoHorizontalList()]),
  spatial(),
];

export const getMiddlewareStrateTop = (
  featNewTVNavigation: boolean,
  options?: BinderMiddlewareOptions,
) => [
  scroll([
    scrollTopOrCenter,
    scrollFocusedIntoHorizontalList(featNewTVNavigation),
  ]),
  ...keepFocusedItemOnTheLeft(featNewTVNavigation, options),
  spatial(),
  dynamic({ listSelector: 'ul' }),
];

export const getMiddlewareLiveGrid = (featNewTVNavigation: boolean) => [
  scroll([
    scrollFocusedIntoVerticalViewport({
      featNewTVNavigation,
    }),
    scrollFocusedIntoHorizontalList(featNewTVNavigation),
  ]),
  ...keepFocusedItemOnTheLeft(featNewTVNavigation),
  spatial(),
];

export const getMiddlewareLiveGridPerso = (featNewTVNavigation: boolean) => [
  dynamic({}),
  scroll([
    scrollFocusedIntoVerticalViewport({
      featNewTVNavigation,
    }),
    scrollFocusedIntoHorizontalList(featNewTVNavigation),
  ]),
  ...keepFocusedItemOnTheLeft(featNewTVNavigation),
  spatial(),
];

export const getMiddlewareStratePerso = (
  featNewTVNavigation: boolean,
  options?: BinderMiddlewareOptions,
) => [
  dynamic({
    listSelector: 'ul',
  }),
  scroll([
    scrollFocusedIntoVerticalViewport({
      featNewTVNavigation,
    }),
    scrollFocusedIntoHorizontalList(featNewTVNavigation),
  ]),
  ...keepFocusedItemOnTheLeft(featNewTVNavigation, options),
  spatial(),
];

export const getMiddlewareEpisodesContainer = (
  featNewTVNavigation: boolean,
) => [
  scroll([scrollFocusedIntoHorizontalList(featNewTVNavigation)]),
  memory(),
  spatial(),
];

export const MIDDLEWARE_MODAL = [
  scroll([
    scrollFocusedIntoVerticalScrollContainer('#modal-scroll-container'),
    scrollFocusedIntoHorizontalList(),
  ]),
  spatial(),
];

export const MIDDLEWARE_MODAL_TOP = [
  scroll([scrollModalTop, scrollFocusedIntoHorizontalList()]),
  spatial(),
];

export const getMiddlewareShowcase = (
  featNewTVNavigation: boolean,
  options?: BinderMiddlewareOptions,
) => [
  scroll([centerBinder, scrollFocusedIntoHorizontalList(featNewTVNavigation)]),
  ...keepFocusedItemOnTheLeft(featNewTVNavigation, options),
  spatial(),
];

export const getMiddlewareShowcaseTop = (
  featNewTVNavigation: boolean,
  options?: BinderMiddlewareOptions,
) => [
  scroll([
    scrollTopOrCenter,
    scrollFocusedIntoHorizontalList(featNewTVNavigation),
  ]),
  ...keepFocusedItemOnTheLeft(featNewTVNavigation, options),
  spatial(),
];

export const MIDDLEWARE_ACTION_LAYOUT = [
  dynamic({ listSelector: 'div' }),
  scroll([scrollTop]),
  spatial(),
];

export const MIDDLEWARE_PROFILES_LIST = [
  scroll([
    scrollFocusedIntoVerticalViewport(),
    scrollFocusedIntoHorizontalList(false, 'ul'),
  ]),
  memory(),
  spatial(),
];

export const MIDDLEWARE_PROFILES_SELECTOR = [
  scroll([
    scrollFocusedIntoVerticalViewport(),
    scrollFocusedIntoHorizontalList(),
  ]),
  enter({ forceFocusIndex: 0, shouldForceFocusOnce: true }),
  memory(),
  spatial(),
];

export const MIDDLEWARE_VIRTUAL_KEYBOARD = [
  scroll([scrollFocusedIntoVerticalViewport()]),
  enter({ forceFocusIndex: 1, shouldForceFocusOnce: true }),
  memory(),
  spatial(),
];

export const MIDDLEWARE_PROFILE = [
  scroll([scrollFocusedIntoVerticalScrollContainer('#modal-scroll-container')]),
  spatial(),
];

export const MIDDLEWARE_FUNNEL_VOD = [
  scroll([
    scrollFocusedIntoVerticalScrollContainer('#modal_funnel_tvod', {
      margins: {
        top: 45, // 720px
        bottom: 17.5, // 280px
      },
    }),
  ]),
  spatial(),
];

export const MIDDLEWARE_FUNNEL_VOD_SEASONS_TABS = [
  scroll([scrollFocusedIntoHorizontalList()]),
  spatial(),
];

export const MIDDLEWARE_FUNNEL_PROMOTIONAL = [
  enter({ forceFocusIndex: 2 }),
  spatial(),
];
export const KEYBOARD_MIDDLEWARE = [memory(), spatial()];

export const MIDDLEWARE_ERROR = [
  dynamic({}),
  enter({ forceFocusIndex: 1, shouldForceFocusOnce: true }),
  spatial(),
];

export const MIDDLEWARE_MOOD = [enter({ forceFocusIndex: 1 }), spatial()];

export const getMiddlewareTimeline = (featNewTVNavigation: boolean) => [
  scroll([
    scrollFocusedIntoVerticalViewport({
      featNewTVNavigation,
    }),
  ]),
  spatial(),
];

export const MIDDLEWARE_SVL = [dynamic({}), spatial()];

export const getMiddlewareStats = (featNewTVNavigation: boolean) => [
  scroll([
    scrollFocusedIntoVerticalViewport({
      featNewTVNavigation,
    }),
  ]),
  spatial(),
];

export const getMiddlewareRanking = (featNewTVNavigation: boolean) => [
  scroll([
    scrollFocusedIntoVerticalViewport({
      featNewTVNavigation,
    }),
  ]),
  spatial(),
];

export const getMiddlewareRating = (featNewTVNavigation: boolean) => [
  scroll([
    scrollFocusedIntoVerticalViewport({
      featNewTVNavigation,
    }),
  ]),
  spatial(),
];
